

import React from 'react';
import { Divider, Grid, Paper, IconButton, Stack, Link } from '@mui/material';
import InstagramIcon from '@mui/icons-material/Instagram';
import YouTubeIcon from '@mui/icons-material/YouTube';
import SchoolIcon from '@mui/icons-material/School';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import XIcon from '@mui/icons-material/X';
import MyImageList from '../Components/MyImageList';
import publications from '../assets/publications.webp';
import radioillustrations from '../assets/radioIllustrations.webp';
import shortVideo from '../assets/shortvideo.webp';
import infoGraphics from '../assets/infographics.webp';
import HomeBg from '../assets/HomeBg.webp';
import blogCard from '../assets/blogcard.jpg';
import MyCard from './MyCard';
import ContactForm from '../Components/ContactForm';
import { Box } from "@mui/system";
import Profile from '../assets/profile.jpg';
import articles from '../assets/articles.jpg';
import img1 from '../assets/1.jpeg';
import img2 from '../assets/2.jpeg';
import img3 from '../assets/3.jpeg';
import img4 from '../assets/4.jpeg';
import img5 from '../assets/5.jpeg';
import img6 from '../assets/6.jpeg';
import Typography from "@mui/material/Typography";
import GoogleAd from '../Components/GoogleAd';
import { AD_CONFIG } from '../config/ads';
import Seo from '../Components/Seo';
import ResourceLearningSummary from '../Components/ResourceLearningSummary';

const imageList = [
    { id: 1, img: img1 },
    { id: 2, img: img2 },
    { id: 3, img: img3 },
    { id: 4, img: img4 },
    { id: 5, img: img5 },
    { id: 6, img: img6 },
];

const Home = () => {
    // const count = useSelector((state: RootState) => state.counter.value)
    const itemData = [
        { id: 1, img: infoGraphics, title: 'InfoGraphics', description: 'Structured visual summaries for radiology signs, staging systems, and diagnostic checklists.', path: '/infographics' },
        { id: 2, img: radioillustrations, title: 'Radio Illustrations', description: 'Original anatomy and pathology diagrams that explain radiology concepts visually.', path: '/radioillustrations' },
        { id: 3, img: articles, title: 'Articles', description: 'Educational articles with radiology teaching notes, figures, and interpretation context.', path: '/articles' },
        { id: 4, img: shortVideo, title: 'Short Videos', description: 'Focused video lessons for high-yield imaging signs and practical review.', path: '/shortVideos' },
        { id: 5, img: blogCard, title: 'Blogs', description: 'Reflections on radiology learning, teaching, medical illustration, and academic practice.', path: '/blogs' },
        { id: 6, img: publications, title: 'Publications', description: 'Academic publications, books, and scholarly work connected to radiology education.', path: '/publications' },
    ];

    // Social media handles - Update these with your actual social media URLs
    const socialMediaLinks = {
        instagram: 'https://www.instagram.com/dr.shubham_arora',
        youtube: 'https://www.youtube.com/@DrShubhamArora',
        researchgate: 'https://www.researchgate.net/profile/Shubham-Arora-11',
        linkedin: 'https://in.linkedin.com/in/dr-shubham-arora-2821aa150',
        twitter: 'https://x.com/Dr_S_Arora',
    };

    // Social media configuration with icons, colors, and labels
    const socialMediaConfig = [
        {
            key: 'instagram',
            url: socialMediaLinks.instagram,
            icon: InstagramIcon,
            color: '#E4405F',
            hoverColor: 'rgba(228, 64, 95, 0.1)',
            label: 'Instagram',
        },
        {
            key: 'youtube',
            url: socialMediaLinks.youtube,
            icon: YouTubeIcon,
            color: '#FF0000',
            hoverColor: 'rgba(255, 0, 0, 0.1)',
            label: 'YouTube',
        },
        {
            key: 'researchgate',
            url: socialMediaLinks.researchgate,
            icon: SchoolIcon,
            color: '#00CCBB',
            hoverColor: 'rgba(0, 204, 187, 0.1)',
            label: 'ResearchGate',
        },
        {
            key: 'linkedin',
            url: socialMediaLinks.linkedin,
            icon: LinkedInIcon,
            color: '#0077B5',
            hoverColor: 'rgba(0, 119, 181, 0.1)',
            label: 'LinkedIn',
        },
        {
            key: 'twitter',
            url: socialMediaLinks.twitter,
            icon: XIcon,
            color: '#000000',
            hoverColor: 'rgba(29, 161, 242, 0.1)',
            label: 'Twitter',
        },
    ];

    return (
        <Grid container spacing={2}>
            <Seo
                title="Radiology Illustration"
                description="Original educational radiology illustrations, infographics, articles, publications, and short learning videos by Dr. Shubham Arora."
                canonicalPath="/"
                jsonLd={{
                    '@context': 'https://schema.org',
                    '@type': 'MedicalWebPage',
                    name: 'Radiology Illustration',
                    url: 'https://radiologyillustration.com/',
                    description: 'Original educational radiology illustrations, infographics, articles, publications, and short learning videos.',
                    author: {
                        '@type': 'Person',
                        name: 'Dr. Shubham Arora',
                    },
                    medicalAudience: ['MedicalStudent', 'Clinician'],
                }}
            />
            {/* Hero image */}
            {/*<div>Counter value is {count}</div>*/}
            <Grid size={{ xs: 12 }}>
                <img
                    src={HomeBg}
                    alt="Radiology Illustration educational banner by Dr. Shubham Arora"
                    style={{ maxWidth: '100%', height: "auto", objectFit: 'cover', display: 'block' }}
                />
            </Grid>

            <Grid size={{ xs: 12 }}>
                <Box sx={{ maxWidth: 960, mx: 'auto', px: { xs: 2, sm: 3 }, textAlign: 'center' }}>
                    <Typography
                        variant="h1"
                        sx={{
                            fontSize: { xs: '2rem', sm: '2.6rem', md: '3.25rem' },
                            fontWeight: 800,
                            lineHeight: 1.15,
                            mb: 1.5,
                        }}
                    >
                        Radiology Illustration
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '1rem', md: '1.15rem' }, lineHeight: 1.8 }}>
                        Visual radiology learning resources by Dr. Shubham Arora, combining original medical illustrations, concise infographics, articles, publications, and short videos for students, trainees, educators, and healthcare professionals.
                    </Typography>
                </Box>
            </Grid>

            {/* Cards */}
            <Grid size={{ xs: 12 }}>
                <Grid
                    container
                    spacing={2}
                    justifyContent="center"
                    alignItems="center"
                >
                    {itemData.map((item) => (
                        <Grid
                            key={item.id}
                            size={{ xs: 10, sm: 6, md: 4, lg: 3 }} // not xs:12 → leaves space
                        >
                            <MyCard
                                id={item.id}
                                name={item.title}
                                description={item.description}
                                url={item.img}
                                path={item.path}
                            />
                        </Grid>
                    ))}
                </Grid>
            </Grid>

            <Grid size={{ xs: 12 }}>
                <ResourceLearningSummary variant="home" />
            </Grid>

            {/* Ad after cards */}
            <Grid size={{ xs: 12 }}>
                <GoogleAd
                    adSlot={AD_CONFIG.adUnits.homePage}
                    format="horizontal"
                    showPlaceholder={false}
                />
            </Grid>


            {/* Profile & Intro */}
            <Grid size={{ xs: 12 }}>
                <Paper elevation={3} sx={{ display: 'flex', alignItems: 'center', mx: { xs: 2, sm: 3, md: 4 } }}>
                    <Grid container spacing={0} justifyContent="center" alignItems="center">

                        {/* Left: Profile Image */}
                        <Grid size={{ xs: 12, md: 5 }} sx={{ pr: { md: 2 } }}>
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                <img src={Profile} height={500} width={400} alt="Dr. Shubham Arora profile portrait" style={{ maxWidth: '100%', height: 'auto' }} />
                            </Box>
                        </Grid>

                        {/* Divider (vertical on desktop, hidden on mobile) */}
                        <Grid size={{ xs: 12, md: 'auto' }} sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'center', px: 0 }}>
                            <Divider orientation="vertical" flexItem />
                        </Grid>

                        {/* Right: Text */}
                        <Grid size={{ xs: 12, md: 5 }} sx={{ pl: { md: 2 } }}>
                            <Typography
                                component="div"
                                sx={{
                                    textAlign: 'center',
                                    fontSize: {
                                        xs: '1rem',
                                        sm: '1.25rem',
                                        md: '1.5rem',
                                        lg: '1.5rem',
                                    },
                                    fontWeight: 500,
                                    mb: 3,
                                }}
                            >
                                Welcome to my radiology corner. A space where complex concepts meet clarity through illustrations, infographics, articles, short videos, and blogs. Whether you're a student or a professional, you'll find visual learning tools designed to make radiology easier and more engaging.
                                <br />-Dr. Shubham Arora, MD, MBBS.
                            </Typography>

                            {/* Social Media Links */}
                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                                <Stack direction="row" spacing={2} alignItems="center">
                                    {socialMediaConfig.map((social) => {
                                        if (!social.url) return null;

                                        const IconComponent = social.icon;

                                        return (
                                            <IconButton
                                                key={social.key}
                                                component={Link}
                                                href={social.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                aria-label={social.label}
                                                sx={{
                                                    color: social.color,
                                                    '&:hover': {
                                                        backgroundColor: social.hoverColor,
                                                        transform: 'scale(1.1)',
                                                    },
                                                    transition: 'all 0.3s ease',
                                                }}
                                            >
                                                <IconComponent sx={{ fontSize: { xs: 24, sm: 28, md: 32 } }} />
                                            </IconButton>
                                        );
                                    })}
                                </Stack>
                            </Box>
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>

            {/* Vision / Mission Section */}
            <Grid size={{ xs: 12 }}>
                <Paper elevation={3} sx={{ p: { xs: 3, md: 5 }, mx: { xs: 2, sm: 3, md: 4 } }}>
                    <Typography variant="h4" component="h2" align="center" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                        Simplifying Radiology
                    </Typography>
                    <Typography variant="body1" align="justify" sx={{ fontSize: '1.1rem', lineHeight: 1.8, mb: 2 }}>
                        This website is built on a simple vision: to make complex radiological concepts clear, visual, and easy to understand. The platform combines illustrations, infographics, articles, short videos, and blogs to transform dense topics into intuitive learning experiences.
                    </Typography>
                    <Typography variant="body1" align="justify" sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}>
                        Designed for both students and professionals, the website offers visual learning tools that enhance recall, simplify difficult anatomy and pathology, and support day-to-day clinical practice.
                    </Typography>
                </Paper>
            </Grid>

            {/* Image list */}
            <Grid size={{ xs: 12 }}>
                <MyImageList itemData={imageList} />
            </Grid>

            {/* Contact Form */}
            <Grid size={{ xs: 12 }} id="contact">
                <ContactForm />
            </Grid>
        </Grid>
    );
};

export default Home;
